<template>
  <div class="album-details-card">
    <div v-if="album" class="album-content-wrapper">
      <div class="album-header">
        <img class="album-thumbnail"
             :src="album.album_thumbnail"
             :alt="`Album cover for ${album.album_name}`" />
        <h3 class="album-title">{{ album.album_name }}</h3>
      </div>

      <div class="album-actions-grid">
        <b-button variant="primary" @click="addSong" class="action-button">
          <i class="fas fa-plus icon-spacing"></i> Add Song
        </b-button>
        <b-button variant="info" @click="editAlbum" class="action-button">
          <i class="fas fa-edit icon-spacing"></i> Edit Album
        </b-button>
        <b-button variant="danger" @click="deleteAlbum" class="action-button">
          <i class="far fa-trash-alt icon-spacing"></i> Delete Album
        </b-button>
        <b-button variant="secondary" @click="notify" class="action-button">
          <i class="far fa-bell icon-spacing"></i> Notify
        </b-button>
      </div>

      <div class="album-order-section">
        <label for="order-select" class="order-label">Order Number:</label>
        <b-form-select id="order-select"
                       class="order-select-dropdown"
                       :options="orderOptions"
                       v-model="order"
                       @change="changeOrder"
                       size="sm">
        </b-form-select>
      </div>

      <song-list :songs="albumSongs"
                 :albumId="albumId"
                 class="album-song-list"/>
    </div>

    <div v-else class="text-center py-5 no-album-data">
      <p class="text-muted">Loading album details...</p>
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <modals-container/>
    <v-dialog/>
  </div>
</template>



<script>
  const AddSong = () => import('./AddSong.vue');
  const EditAlbum = () => import('./EditAlbum.vue');
  const SongList = () => import('./SongList.vue');
  import { mapGetters, mapActions } from 'vuex';

  export default {
    name  : 'AlbumCard',
    props : {
      albumId    : Number,
      albumTotal : Number,
    },
    data() {
      return {
        order : null,
      }
    },
    computed : {
      ...mapGetters({
        album      : 'media/album',
        albumSongs : 'media/albumSongs',
      }),
      orderOptions() {
        const options = [{
          value    : null,
          text     : 'Select Order',
          disabled : true,
        }];
        for (let i = 1; i <= this.albumTotal; i++) {
          options.push({
            value : i,
            text  : i,
          });
        }
        return options;
      },
    },
    components : {
      SongList,
    },
    watch : {
      albumId() {
        this.getAlbum(this.albumId);
      },
      album(val) {
        this.order = val.order_no;
      },
    },
    methods : {
      ...mapActions({
        'getAlbum' : 'media/getAlbum',
      }),

      /**
       * Show Add Song Modal
       */
      addSong() {
        this.$modal.show(
          AddSong,
          { album : this.album },
          { height : 'auto', adaptive : true }
        );
      },

      /**
       * Show Edit Album Modal
       */
      editAlbum() {
        this.$modal.show(
          EditAlbum,
          { album : this.album },
          { height : 'auto', adaptive : true },
          // eslint-disable-next-line no-unused-vars
          { 'before-close' : async (event) => {
            this.getAlbum(this.album.mobile_album_id);
            const albumData = {
              id        : this.album.mobile_album_id,
              albumName : this.album.album_name,
              thumb     : this.album.album_thumbnail,
              orderNo   : this.album.order_no,
            };
            this.$emit('update-selected-album', albumData);
          }},
        );
      },

      /**
       * Delete Album
       */
      deleteAlbum() {
        this.$modal.show('dialog', {
          title : 'Please confirm:',
          text  : `Are you sure you want to delete ` +
            `<b>${this.album.album_name}</b>?`,
          buttons : [
            {
              title   : 'Yes',
              handler : () => {
                this.$http.delete('/api/album', {
                  data : {
                    id : this.album.mobile_album_id,
                  },
                }).then(() => {
                  this.$notify({
                    group : 'notif',
                    type  : 'success',
                    title : 'Success!',
                    text  : 'Deleted ' + this.album.album_name +
                      ' successfully',
                  });
                  this.$emit('delete-album-update');
                  this.$modal.hide('dialog');
                }).catch(() => {
                  this.$notify({
                    group : 'notif',
                    type  : 'error',
                    title : 'Failed',
                    text  : 'Oops! Something went wrong!',
                  });
                });
              },
            },
            { title : 'No' },
          ],
        });
      },

      /**
       * Send Notification
       */
      notify() {
        this.$modal.show('dialog', {
          title : 'Send Notification',
          text  : `Send New Music Notification for ` +
            `<b>${this.album.album_name}</b>?`,
          buttons : [ {
            title   : 'Yes',
            handler : () => {
              this.$http.post('/api/notification/admin/new_music', {
                mobileAlbumId : this.album.mobile_album_id,
                // albumName: "random album",
                // mobileAlbumThumbnail: "cdvsrver"
              }).then(() => {
                this.$notify({
                  group : 'notif',
                  type  : 'success',
                  title : 'Success!',
                  text  : 'Notification sent.',
                });
                this.$modal.hide('dialog');
              }).catch(() => {
                this.$notify({
                  group : 'notif',
                  type  : 'error',
                  title : 'Failed',
                  text  : 'Oops! Something went wrong!',
                });
              });
            },
          }, {
            title : 'No',
          } ],
        });
      },

      /**
       * Change mobile album order
       * @param data
       */
      changeOrder(data) {
        this.$http.post('api/setAlbumOrderNumber', {
          mobileAlbumId : this.albumId,
          orderNo       : data,
        }).then((res) => {
          this.$notify({
            group : 'notif',
            type  : 'success',
            title : 'Success',
            text  : 'Order successfully updated',
          });
          const albumData = {
            id        : res.data.album.mobile_album_id,
            albumName : res.data.album.album_name,
            thumb     : res.data.album.album_thumbnail,
            orderNo   : res.data.album.order_no,
          };
          this.$emit('update-selected-album', albumData);
        }).catch(() => {
          this.$notify({
            group : 'notif',
            type  : 'error',
            title : 'Failed',
            text  : 'Oops! Something went wrong! Please try again.',
          });
        });
      },
    },
    mounted() {
      this.getAlbum(this.albumId);
    },
  }
</script>

<style lang="scss" scoped>
  @import "../../../assets/scss/components/admin/mobile_music/album-card";
  // Import variables for consistent styling
  //@import "../../../assets/scss/custom-variables"; // Assuming you have a file for custom variables
  // If you don't have custom variables, define them here:
  $primary-color: #1a73e8; // Example primary blue
  $success-color: #2dce89; // Example success green
  $info-color: #11c1e5;   // Example info blue
  $danger-color: #f5365c; // Example danger red
  $secondary-color: #8898aa; // Example secondary gray
  $light-bg: #f8f9fe; // Light background for cards/sections
  $border-color: #e9ecef;
  $text-dark: #32325d;
  $text-muted: #8898aa;
  $card-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  $border-radius-base: 0.375rem;

  // Album card specific styles (this is the outer container from AlbumList)
  .album-details-card {
    // background-color: #ffffff;
    // border-radius: $border-radius-base * 1.5; // Slightly larger border-radius
    // padding: 1.5rem; // Generous padding
    // box-shadow: $card-shadow; // Consistent shadow
    // border: 1px solid $border-color;
    position: relative; // For positioning loading spinner if needed
    min-height: 400px; // Ensure a minimum height for consistent layout
    display: flex;
    flex-direction: column;
    justify-content: space-between; // Distribute content vertically
  }

  .album-content-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1rem; // Space between main sections
  }

  // Album Header (Image and Title)
  .album-header {
    display: flex;
    align-items: center;
    gap: 1rem; // Space between image and title
    // margin-bottom: 1rem; // Space below header section
    border-bottom: 1px solid $border-color; // Separator
    padding-bottom: 1rem; // Padding for separator
  }

  .album-thumbnail {
    width: 90px; // Fixed size for the thumbnail
    height: 90px;
    object-fit: cover;
    border-radius: $border-radius-base; // Rounded corners for the image
    box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.08); // Soft shadow for the image
    flex-shrink: 0; // Prevent shrinking on smaller screens
  }

  .album-title {
    font-size: 1rem; // Larger, more prominent title
    font-weight: 500;
    color: $text-dark;
    margin: 0; // Remove default margin
    line-height: 1.2; // Adjust line height
    word-break: break-word; // Ensure long titles wrap
  }

  // Album Actions (Buttons)
  // .album-actions-grid {
  //   display: grid;
  //   grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); // Responsive grid for buttons
  //   gap: 0.75rem; // Space between buttons
  //   margin-bottom: 1.5rem; // Space below button section
    
  //   .action-button {
  //     font-size: 0.9rem;
  //     font-weight: 600;
  //     padding: 0.6rem 0.8rem;
  //     border-radius: $border-radius-base;
  //     transition: all 0.2s ease-in-out;
  //     display: flex;
  //     align-items: center;
  //     justify-content: center;
  //     text-align: center; // Ensure text is centered
      
  //     i {
  //       font-size: 1rem;
  //       // Specific colors for icons to match button variant (optional, but good UX)
  //       // For example, if using Bootstrap's default variants:
  //       &.fas.fa-plus { color: white; } // Assuming primary/success button background makes icon white
  //       &.fas.fa-edit { color: white; } // Assuming info button
  //       &.far.fa-trash-alt { color: white; } // Assuming danger button
  //       &.far.fa-bell { color: white; } // Assuming secondary button
  //     }
      
  //     // Override Bootstrap's default outlines for a cleaner look or use filled buttons
  //     &.btn-primary { background-color: $primary-color; border-color: $primary-color; color: white; }
  //     &.btn-info { background-color: $info-color; border-color: $info-color; color: white; }
  //     &.btn-danger { background-color: $danger-color; border-color: $danger-color; color: white; }
  //     &.btn-secondary { background-color: $secondary-color; border-color: $secondary-color; color: white; }

  //     // Hover effects
  //     &:hover {
  //       transform: translateY(-2px);
  //       box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  //     }
  //   }
    
  //   .icon-spacing {
  //     margin-right: 0.5rem; // Space between icon and text
  //   }
  // }

  // Album Actions (Buttons)
  .album-actions-grid {
    // ESTO ES LO CLAVE: Siempre dos columnas de igual ancho
    display: grid;
    grid-template-columns: 1fr 1fr;
    
    gap: 0.75rem; // Espacio entre botones
    margin-bottom: 0; // Espacio debajo de la sección de botones

    .action-button {
      width: 100%;
      font-size: 0.9rem;
      font-weight: 600;
      padding: 0.6rem 0.8rem;
      border-radius: $border-radius-base;
      transition: all 0.2s ease-in-out;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;

      i {
        font-size: 1rem;
      }

      // Colores de los botones (asegúrate de que estas variables estén definidas)
      &.btn-primary { background-color: $primary-color; border-color: $primary-color; color: white; }
      &.btn-info { background-color: $info-color; border-color: $info-color; color: white; }
      &.btn-danger { background-color: $danger-color; border-color: $danger-color; color: white; }
      &.btn-secondary { background-color: $secondary-color; border-color: $secondary-color; color: white; }

      // Hover effects
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
      }
    }

    .icon-spacing {
      margin-right: 0.5rem; // Espacio entre el ícono y el texto
    }
  }

  // Album Order Section
  .album-order-section {
    display: flex;
    align-items: center;
    gap: 0.75rem; // Space between label and select
    margin-bottom: 0; // Space below this section

    .order-label {
      font-weight: 600;
      color: $text-dark;
      margin: 0; // Remove default margin
      font-size: 0.95rem;
    }

    .order-select-dropdown {
      flex-grow: 1; // Allow select box to take available space
      max-width: 150px; // Limit width for better aesthetics
      border-radius: $border-radius-base;
      border-color: $border-color;
      font-size: 0.9rem;
      padding: 0.375rem 0.75rem; // Adjust padding to match button height if possible
      height: auto; // Let content define height
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e"); // Custom arrow for select
      background-repeat: no-repeat;
      background-position: right 0.75rem center;
      background-size: 16px 12px;
      -webkit-appearance: none; // Remove default browser styling
      -moz-appearance: none;
      appearance: none;

      &:focus {
        border-color: $primary-color;
        box-shadow: 0 0 0 0.25rem rgba($primary-color, 0.25);
      }
    }
  }

  // Song List styling
  .album-song-list {
    // margin-top: 1rem; // Space above the song list
    // Additional styles for SongList component if needed,
    // though SongList.vue should manage its own internal layout
  }

  // No Album Selected / Loading State
  .no-album-data {
    color: $text-muted;
    font-style: italic;
    min-height: 200px; // Ensure it takes some vertical space
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;

    .spinner-border {
      width: 2rem;
      height: 2rem;
      color: $primary-color !important; // Ensure spinner color matches primary
    }
  }

  // Responsive adjustments for smaller screens
  @media (max-width: 768px) { // Adjust for typical mobile screens
    .album-details-card {
      padding: 1rem; // Less padding on smaller screens
    }

    .album-header {
      flex-direction: column; // Stack image and title vertically
      text-align: center;
      .album-thumbnail {
        margin-bottom: 0.5rem; // Space below image when stacked
      }
    }

    .album-title {
      font-size: 1.3rem; // Slightly smaller title on mobile
    }

    .album-actions-grid {
      grid-template-columns: 1fr; // Stack buttons vertically
      .action-button {
        width: 100%; // Make buttons full width
      }
    }

    .album-order-section {
      flex-direction: column; // Stack label and select vertically
      align-items: flex-start; // Align items to the start
      .order-select-dropdown {
        max-width: 100%; // Allow select to take full width
      }
    }
  }
</style>